import Vue from "vue";
import Router from "vue-router";
import OfficialSite from "@/components/OfficialSite";
import {
  getAdminConsoleRouteMenus,
  getAdminConsoleChildPath
} from "@/components/admin/adminConsoleMenu";

Vue.use(Router);

function isFilePreviewMode() {
  return typeof window !== "undefined" && window.location.protocol === "file:";
}

function getFilePreviewRoute() {
  if (!isFilePreviewMode()) {
    return "/";
  }

  const pathname = decodeURIComponent(window.location.pathname || "").replace(
    /\\/g,
    "/"
  );
  const marker = "/dist/";
  const markerIndex = pathname.toLowerCase().lastIndexOf(marker);

  if (markerIndex === -1) {
    return "/";
  }

  const tailPath = pathname
    .slice(markerIndex + marker.length)
    .replace(/^\/+/, "");

  if (!tailPath || /^index\.html$/i.test(tailPath)) {
    return "/";
  }

  return "/" + tailPath;
}

if (
  isFilePreviewMode() &&
  (!window.location.hash || window.location.hash === "#")
) {
  window.location.hash = getFilePreviewRoute();
}

function loadAsyncComponent(loader) {
  return function(resolve) {
    loader(function(module) {
      resolve(module.default || module);
    });
  };
}

function createAdminConsoleRoutePage(loader, options) {
  const RoutePage = loadAsyncComponent(loader);
  const propKeys = (options && options.props) || [];
  const eventMap = (options && options.events) || {};

  return {
    inject: ["adminConsoleContext"],
    components: {
      RoutePage
    },
    computed: {
      routePageProps() {
        const context = this.adminConsoleContext || {};
        return propKeys.reduce((result, key) => {
          result[key] = context[key];
          return result;
        }, {});
      },
      routePageListeners() {
        const context = this.adminConsoleContext || {};
        return Object.keys(eventMap).reduce((result, eventName) => {
          const methodName = eventMap[eventName];
          if (typeof context[methodName] === "function") {
            result[eventName] = context[methodName].bind(context);
          }
          return result;
        }, {});
      }
    },
    render(h) {
      return h("RoutePage", {
        props: this.routePageProps,
        on: this.routePageListeners
      });
    }
  };
}

const AdminLogin = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(require("@/components/AdminLoginEntry"));
    },
    "admin-auth"
  );
});
const AdminConsole = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(require("@/components/AdminConsole"));
    },
    "admin-console"
  );
});
const AdminAgentUnbound = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(require("@/components/AdminAgentUnbound"));
    },
    "admin-auth"
  );
});
const AdminConsoleOverviewPage = createAdminConsoleRoutePage(
  function(resolve) {
    require.ensure(
      [],
      function() {
        resolve(require("@/components/admin/pages/OverviewPage"));
      },
      "admin-console"
    );
  },
  {
    props: [
      "summaryCards",
      "agentProfile",
      "userProfile",
      "enterpriseCustomers",
      "renewalBoard",
      "reminders",
      "notifications",
      "permissionScopes",
      "warehouseInventory",
      "performanceTrend",
      "channelMix",
      "serviceDistribution",
      "currentDate",
      "actionLogs"
    ],
    events: {
      navigate: "handlePageNavigate"
    }
  }
);
const AdminConsoleEnterpriseListPage = createAdminConsoleRoutePage(
  function(resolve) {
    require.ensure(
      [],
      function() {
        resolve(require("@/components/admin/pages/EnterpriseListPage"));
      },
      "admin-console"
    );
  },
  {
    props: ["enterpriseCustomers", "industries", "warehouseInventory"],
    events: {
      navigate: "handlePageNavigate",
      "prefill-upgrade": "prefillUpgrade",
      "prefill-ledger": "prefillLedger"
    }
  }
);
const AdminConsoleRenewalsPage = createAdminConsoleRoutePage(
  function(resolve) {
    require.ensure(
      [],
      function() {
        resolve(require("@/components/admin/pages/RenewalsPage"));
      },
      "admin-console"
    );
  },
  {
    props: ["renewalBoard", "expiringCustomers", "planOptions"],
    events: {
      "mark-renewal-done": "markRenewalDone"
    }
  }
);
const AdminConsoleWarehouseStockPage = createAdminConsoleRoutePage(
  function(resolve) {
    require.ensure(
      [],
      function() {
        resolve(require("@/components/admin/pages/WarehouseStockPage"));
      },
      "admin-console"
    );
  },
  {
    props: [
      "warehouseOverviewCards",
      "warehouseInventory",
      "warehouseLowStockItems",
      "warehouseTransactions"
    ],
    events: {
      navigate: "handlePageNavigate",
      "open-warehouse-purchase": "openWarehousePurchase"
    }
  }
);
const AdminConsolePermissionsPage = createAdminConsoleRoutePage(
  function(resolve) {
    require.ensure(
      [],
      function() {
        resolve(require("@/components/admin/pages/PermissionsPage"));
      },
      "admin-console"
    );
  },
  {
    props: ["permissionScopes"]
  }
);
const AdminConsoleLogsPage = createAdminConsoleRoutePage(
  function(resolve) {
    require.ensure(
      [],
      function() {
        resolve(require("@/components/admin/pages/LogsPage"));
      },
      "admin-console"
    );
  },
  {
    props: ["actionLogs"]
  }
);
const PlatformAdminLogin = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(require("@/components/PlatformAdminLogin"));
    },
    "platform-auth"
  );
});
const PlatformAdminConsole = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(require("@/components/PlatformAdminConsole"));
    },
    "platform-console"
  );
});
const PlatformUsersListPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformUsersListPage")
      );
    },
    "platform-console"
  );
});
const PlatformEnterpriseCertificationsPage = loadAsyncComponent(function(
  resolve
) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformEnterpriseCertificationsPage")
      );
    },
    "platform-console"
  );
});
const PlatformEnterpriseAdminsPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformEnterpriseAdminsPage")
      );
    },
    "platform-console"
  );
});
// const PlatformAgentsPage = loadAsyncComponent(function (resolve) {
//   require.ensure([], function () {
//     resolve(require('@/components/platform-admin/pages/PlatformAgentsPage'))
//   }, 'platform-console')
// })
const PlatformEnterprisesPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformEnterprisesPage")
      );
    },
    "platform-console"
  );
});
const PlatformApprovalsPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformApprovalsPage")
      );
    },
    "platform-console"
  );
});
const PlatformSettlementsPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformSettlementsPage")
      );
    },
    "platform-console"
  );
});
const PlatformControlPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(require("@/components/platform-admin/pages/PlatformControlPage"));
    },
    "platform-console"
  );
});
const PlatformNotificationsPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformNotificationsPage")
      );
    },
    "platform-console"
  );
});
const PlatformProfilePage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(require("@/components/platform-admin/pages/PlatformProfilePage"));
    },
    "platform-console"
  );
});
const PlatformPasswordPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformPasswordPage")
      );
    },
    "platform-console"
  );
});
// 代理商管理模块
const PlatformAgentsListPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformAgentsListPage")
      );
    },
    "platform-console"
  );
});
const PlatformAgentCustomersPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformAgentCustomersPage")
      );
    },
    "platform-console"
  );
});
const PlatformAgentUserPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformAgentsUserPage")
      );
    },
    "platform-console"
  );
});
const PlatformAgentRolePage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformAgentsRolePage")
      );
    },
    "platform-console"
  );
});
// 套餐管理模块
const PlatformPackagesListPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformPackagesListPage")
      );
    },
    "platform-console"
  );
});
const PlatformPackageBenefitsPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformPackageBenefitsPage")
      );
    },
    "platform-console"
  );
});
const PlatformSeatRulesPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformPackageRulesPage")
      );
    },
    "platform-console"
  );
});
// 订单管理模块
const PlatformOrdersListPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformOrdersListPage")
      );
    },
    "platform-console"
  );
});
const PlatformOrdersNewPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformOrdersNewPage")
      );
    },
    "platform-console"
  );
});
const PlatformOrdersRenewalPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformOrdersRenewalPage")
      );
    },
    "platform-console"
  );
});
const PlatformOrdersAddonPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformOrdersAddonPage")
      );
    },
    "platform-console"
  );
});
// 权限与账号模块
const PlatformAccessAccountsPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformAccessAccountsPage")
      );
    },
    "platform-console"
  );
});
const PlatformAccessRolesPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformAccessRolesPage")
      );
    },
    "platform-console"
  );
});
// 应用商店模块
const PlatformStoreListPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformStoreListPage")
      );
    },
    "platform-console"
  );
});
const PlatformStoreCategoryPage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformStoreCategoryPage")
      );
    },
    "platform-console"
  );
});
const PlatformStorePurchasePage = loadAsyncComponent(function(resolve) {
  require.ensure(
    [],
    function() {
      resolve(
        require("@/components/platform-admin/pages/PlatformStorePurchasePage")
      );
    },
    "platform-console"
  );
});

const router = new Router({
  mode: isFilePreviewMode() ? "hash" : "history",
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: "/",
      name: "OfficialSite",
      component: OfficialSite,
      meta: {
        title: "数企推 | 企业数字名片、企业官网与线索转化解决方案",
        description:
          "数企推为企业提供数字名片、手机官网、品牌展示、内容分发与线索转化解决方案，帮助市场、销售、服务与渠道团队统一对外形象并持续获客。",
        robots:
          "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
      }
    },
    {
      path: "/admin/login/index.html",
      name: "AdminLogin",
      component: AdminLogin,
      meta: {
        title: "后台登录 | 数企推",
        description: "数企推后台登录入口。",
        robots: "noindex,nofollow,noarchive",
        structuredData: false
      }
    },
    {
      path: "/admin",
      redirect: "/admin/console/index.html"
    },
    {
      path: "/admin/console/index.html",
      name: "AdminConsole",
      component: AdminConsole,
      meta: {
        title: "管理后台 | 数企推",
        description: "数企推管理后台。",
        robots: "noindex,nofollow,noarchive",
        structuredData: false
      }
    },
    {
      path: "/admin/agent-unbound/index.html",
      name: "AdminAgentUnbound",
      component: AdminAgentUnbound,
      meta: {
        title: "未绑定代理商 | 数企推",
        description: "当前账号未绑定代理商，请联系管理员。",
        robots: "noindex,nofollow,noarchive",
        structuredData: false
      }
    },
    {
      path: "/platform-admin",
      redirect: "/platform-admin/login/index.html"
    },
    {
      path: "/platform-admin/index.html",
      redirect: "/platform-admin/login/index.html"
    },
    {
      path: "/platform-admin/login/index.html",
      name: "PlatformAdminLogin",
      component: PlatformAdminLogin,
      meta: {
        title: "平台总后台登录 | 数企推",
        description: "数企推平台管理员登录入口。",
        robots: "noindex,nofollow,noarchive",
        structuredData: false
      }
    },
    {
      path: "/platform-admin/console/index.html",
      redirect: "/platform-admin/console/users/list/index.html"
    },
    {
      path: "/platform-admin/console",
      component: PlatformAdminConsole,
      children: [
        // ========== 用户管理模块 ==========
        {
          path: "users/list/index.html",
          name: "PlatformAdminUsersList",
          component: PlatformUsersListPage,
          meta: {
            title: "用户列表 | 数企推",
            description: "查看个人用户、企业关系和主账号/管理员关系。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "1",
            nodeId: "2",
            viewLabel: "用户列表",
            viewKicker: "用户管理",
            viewShort: "users-list",
            viewDesc: "查看个人用户、企业关系和主账号/管理员关系。"
          }
        },
        // ========== 企业管理模块 ==========
        {
          path: "enterprises/list/index.html",
          name: "PlatformAdminEnterprisesList",
          component: PlatformEnterprisesPage,
          meta: {
            title: "企业列表 | 数企推",
            description: "查看企业主体、创建者、主账号和套餐状态。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "3",
            nodeId: "4",
            viewLabel: "企业列表",
            viewKicker: "企业管理",
            viewShort: "enterprises-list",
            viewDesc: "查看企业主体、创建者、主账号和套餐状态。"
          }
        },
        {
          path: "enterprises/certifications/index.html",
          name: "PlatformAdminEnterpriseCertifications",
          component: PlatformEnterpriseCertificationsPage,
          meta: {
            title: "认证审核 | 数企推",
            description: "处理企业认证状态与认证审核动作。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "3",
            nodeId: "5",
            viewLabel: "认证审核",
            viewKicker: "企业管理",
            viewShort: "enterprise-certifications",
            viewDesc: "处理企业认证状态与认证审核动作。"
          }
        },
        {
          path: "enterprises/admins/index.html",
          name: "PlatformAdminEnterpriseAdmins",
          component: PlatformEnterpriseAdminsPage,
          meta: {
            title: "企业管理员管理 | 数企推",
            description: "管理企业成员关系、角色和主账号转移。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "3",
            nodeId: "6",
            viewLabel: "企业管理员管理",
            viewKicker: "企业管理",
            viewShort: "enterprise-admins",
            viewDesc: "管理企业成员关系、角色和主账号转移。"
          }
        },
        // ========== 代理商管理模块 ==========
        {
          path: "agents/list/index.html",
          name: "PlatformAdminAgentsList",
          component: PlatformAgentsListPage,
          meta: {
            title: "代理商列表 | 数企推",
            description: "查看代理商主体、等级、经营状态。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "7",
            nodeId: "8",
            viewLabel: "代理商列表",
            viewKicker: "代理商管理",
            viewShort: "agents-list",
            viewDesc: "查看代理商主体、等级、经营状态。"
          }
        },
        {
          path: "agents/customers/index.html",
          name: "PlatformAdminAgentCustomers",
          component: PlatformAgentCustomersPage,
          meta: {
            title: "代理商客户企业 | 数企推",
            description: "查看代理商名下客户企业与权益配置。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "7",
            nodeId: "9",
            viewLabel: "代理商客户企业",
            viewKicker: "代理商管理",
            viewShort: "agent-customers",
            viewDesc: "查看代理商名下客户企业与权益配置。"
          }
        },
        {
          path: "agents/users/index.html",
          name: "PlatformAdminAgentUsers",
          component: PlatformAgentUserPage,
          meta: {
            title: "代理商用户 | 数企推",
            description: "查看代理商名下用户与权益配置。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "7",
            nodeId: "24",
            viewLabel: "代理商用户",
            viewKicker: "代理商管理",
            viewShort: "agent-user",
            viewDesc: "查看代理商名下用户与权益配置。"
          }
        },
        {
          path: "agents/role/index.html",
          name: "PlatformAdminAgentRole",
          component: PlatformAgentRolePage,
          meta: {
            title: "代理商角色 | 数企推",
            description: "查看代理商名下角色与权益配置。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "7",
            nodeId: "25",
            viewLabel: "代理商角色",
            viewKicker: "代理商管理",
            viewShort: "agent-role",
            viewDesc: "查看代理商名下角色与权益配置。"
          }
        },
        // ========== 套餐管理模块 ==========
        {
          path: "packages/list/index.html",
          name: "PlatformAdminPackagesList",
          component: PlatformPackagesListPage,
          meta: {
            title: "套餐列表 | 数企推",
            description: "查看套餐主数据、价格和配置。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "10",
            nodeId: "11",
            viewLabel: "套餐列表",
            viewKicker: "套餐管理",
            viewShort: "packages-list",
            viewDesc: "查看套餐主数据、价格和配置。"
          }
        },
        {
          path: "packages/benefits/index.html",
          name: "PlatformAdminPackageBenefits",
          component: PlatformPackageBenefitsPage,
          meta: {
            title: "套餐权益配置 | 数企推",
            description: "统一管理额度、功能和服务。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "10",
            nodeId: "12",
            viewLabel: "套餐权益配置",
            viewKicker: "套餐管理",
            viewShort: "package-benefits",
            viewDesc: "统一管理额度、功能和服务。"
          }
        },
        {
          path: "packages/seat-rules/index.html",
          name: "PlatformAdminSeatRules",
          component: PlatformSeatRulesPage,
          meta: {
            title: "坐席规则配置 | 数企推",
            description: "统一管理员工席位、扩容和规则。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "10",
            nodeId: "13",
            viewLabel: "坐席规则配置",
            viewKicker: "套餐管理",
            viewShort: "seat-rules",
            viewDesc: "统一管理员工席位、扩容和规则。"
          }
        },
        // ========== 订单管理模块 ==========
        {
          path: "orders/list/index.html",
          name: "PlatformAdminOrdersList",
          component: PlatformOrdersListPage,
          meta: {
            title: "订单列表 | 数企推",
            description: "查看全部订单、支付状态和详情。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "14",
            nodeId: "15",
            viewLabel: "订单列表",
            viewKicker: "订单管理",
            viewShort: "orders-list",
            viewDesc: "查看全部订单、支付状态和详情。"
          }
        },
        {
          path: "orders/new/index.html",
          name: "PlatformAdminOrdersNew",
          component: PlatformOrdersNewPage,
          meta: {
            title: "开通订单 | 数企推",
            description: "聚焦新客户首购开通和套餐。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "14",
            nodeId: "16",
            viewLabel: "开通订单",
            viewKicker: "订单管理",
            viewShort: "orders-new",
            viewDesc: "聚焦新客户首购开通和套餐。"
          }
        },
        {
          path: "orders/renewal/index.html",
          name: "PlatformAdminOrdersRenewal",
          component: PlatformOrdersRenewalPage,
          meta: {
            title: "续费订单 | 数企推",
            description: "聚焦企业续费、续期开通。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "14",
            nodeId: "17",
            viewLabel: "续费订单",
            viewKicker: "订单管理",
            viewShort: "orders-renewal",
            viewDesc: "聚焦企业续费、续期开通。"
          }
        },
        {
          path: "orders/addon/index.html",
          name: "PlatformAdminOrdersAddon",
          component: PlatformOrdersAddonPage,
          meta: {
            title: "增购订单 | 数企推",
            description: "聚焦企业增购、配置升级。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "14",
            nodeId: "18",
            viewLabel: "增购订单",
            viewKicker: "订单管理",
            viewShort: "orders-addon",
            viewDesc: "聚焦企业增购、配置升级。"
          }
        },
        // ========== 权限与账号模块 ==========
        {
          path: "access/accounts/index.html",
          name: "PlatformAdminAccessAccounts",
          component: PlatformAccessAccountsPage,
          meta: {
            title: "平台账号管理 | 数企推",
            description: "查看平台账号管理。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "19",
            nodeId: "20",
            viewLabel: "平台账号管理",
            viewKicker: "权限与账号",
            viewShort: "access-accounts",
            viewDesc: "查看平台账号管理。"
          }
        },
        {
          path: "access/roles/index.html",
          name: "PlatformAdminAccessRoles",
          component: PlatformAccessRolesPage,
          meta: {
            title: "角色权限管理 | 数企推",
            description: "查看角色权限管理。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "19",
            nodeId: "21",
            viewLabel: "角色权限管理",
            viewKicker: "权限与账号",
            viewShort: "access-roles",
            viewDesc: "查看角色权限管理。"
          }
        },
        // ========== 应用商店模块 ==========
        {
          path: "store/list/index.html",
          name: "PlatformAdminStoreList",
          component: PlatformStoreListPage,
          meta: {
            title: "应用商店列表 | 数企推",
            description: "查看应用商店列表。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "26",
            nodeId: "27",
            viewLabel: "应用商店列表",
            viewKicker: "应用商店管理",
            viewShort: "store-list",
            viewDesc: "查看应用商店列表。"
          }
        },
        {
          path: "store/category/index.html",
          name: "PlatformAdminStoreCategory",
          component: PlatformStoreCategoryPage,
          meta: {
            title: "应用商店分类管理 | 数企推",
            description: "查看应用商店分类管理。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "26",
            nodeId: "28",
            viewLabel: "应用商店分类管理",
            viewKicker: "应用商店管理",
            viewShort: "store-category",
            viewDesc: "查看应用商店分类管理。"
          }
        },
        {
          path: "store/purchase/index.html",
          name: "PlatformAdminStorePurchase",
          component: PlatformStorePurchasePage,
          meta: {
            title: "应用商店购买记录管理 | 数企推",
            description: "查看应用商店购买记录管理。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "26",
            nodeId: "29",
            viewLabel: "应用商店购买记录管理",
            viewKicker: "应用商店管理",
            viewShort: "store-purchase",
            viewDesc: "查看应用商店购买记录管理。"
          }
        },
        // ========== 原有页面 ==========
        {
          path: "approvals/index.html",
          name: "PlatformAdminApprovals",
          component: PlatformApprovalsPage,
          meta: {
            title: "审核中心 | 数企推",
            description: "平台代理准入、企业认证与变更审批中心。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "4",
            viewLabel: "审核中心",
            viewKicker: "审核中心",
            viewShort: "approvals",
            viewDesc: "平台代理准入、企业认证与变更审批中心。"
          }
        },
        {
          path: "settlements/index.html",
          name: "PlatformAdminSettlements",
          component: PlatformSettlementsPage,
          meta: {
            title: "结算中心 | 数企推",
            description: "平台代理商分润、开票、对账与打款管理。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            sectionId: "5",
            viewLabel: "结算中心",
            viewKicker: "结算中心",
            viewShort: "settlements",
            viewDesc: "平台代理商分润、开票、对账与打款管理。"
          }
        },
        {
          path: "notifications/index.html",
          name: "PlatformAdminNotifications",
          component: PlatformNotificationsPage,
          meta: {
            title: "通知中心 | 数企推",
            description: "平台通知、提醒与消息详情。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            viewLabel: "通知中心",
            viewKicker: "消息中心",
            viewShort: "Notice",
            viewDesc: "查看平台通知、业务提醒、安全消息与详细信息。"
          }
        },
        {
          path: "profile/index.html",
          name: "PlatformAdminProfile",
          component: PlatformProfilePage,
          meta: {
            title: "用户信息 | 数企推",
            description: "平台管理员用户信息与权限范围。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            viewLabel: "用户信息",
            viewKicker: "账号中心",
            viewShort: "Profile",
            viewDesc: "查看和维护平台管理员资料、权限范围与最近登录信息。"
          }
        },
        {
          path: "password/index.html",
          name: "PlatformAdminPassword",
          component: PlatformPasswordPage,
          meta: {
            title: "修改密码 | 数企推",
            description: "平台管理员登录密码与安全设置。",
            robots: "noindex,nofollow,noarchive",
            structuredData: false,
            viewLabel: "修改密码",
            viewKicker: "账号安全",
            viewShort: "Password",
            viewDesc: "更新平台管理员登录密码，并同步平台安全策略。"
          }
        }
      ]
    }
  ]
});

// ====================== 路由守卫（已完整添加）======================
// 白名单：不需要登录就能访问
const whiteList = [
  "/",
  "/admin/login/index.html",
  "/platform-admin/login/index.html"
];

// 全局前置守卫
router.beforeEach((to, from, next) => {
  // 自动设置页面标题
  if (to.meta.title) {
    document.title = to.meta.title;
  }

  // 判断登录状态 - 三种不同的token
  const hasToken =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const hasAdminToken =
    localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");
  const hasPlatformAdminToken =
    localStorage.getItem("platformAdminToken") ||
    sessionStorage.getItem("platformAdminToken");

  // 访问平台管理员登录页且已登录
  if (to.path === "/platform-admin/login/index.html") {
    if (hasPlatformAdminToken) {
      next("/platform-admin/console/index.html");
      return;
    }
    next();
    return;
  }

  // 访问普通管理员登录页且已登录
  if (to.path === "/admin/login/index.html") {
    if (hasAdminToken) {
      next("/admin/console/index.html");
      return;
    }
    next();
    return;
  }

  // 访问代理商未绑定提示页
  if (to.path === "/admin/agent-unbound/index.html") {
    if (hasAdminToken) {
      next();
    } else {
      next("/admin/login/index.html");
    }
    return;
  }

  // 访问白名单页面（已排除登录页）
  if (whiteList.includes(to.path)) {
    next();
    return;
  }

  // 访问平台管理员控制台
  if (to.path.includes("/platform-admin/console")) {
    if (hasPlatformAdminToken) {
      next();
    } else {
      next("/platform-admin/login/index.html");
    }
    return;
  }

  // 访问普通管理员控制台
  if (to.path.includes("/admin/console/index.html")) {
    if (hasAdminToken) {
      next();
    } else {
      next("/admin/login/index.html");
    }
    return;
  }

  // 其他页面
  next();
});

// 全局后置守卫
router.afterEach(() => {
  // 路由切换结束可做收尾操作
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js